import React, { useCallback, useMemo, useState } from 'react';
import { Dialog, DialogContent, makeStyles } from '@material-ui/core';
import {
  BodyTextMedium,
  BodyTextSmall,
  DeprecatedButton,
  DeprecatedButtonGroup,
  HeadingText20,
  HeadingText50,
  RadioGroup,
} from '@modernhealth/vital-components';
import { useHistory } from 'react-router-dom';

import type { InvoicingPageStepProps } from '../types';
import { InfoCallout } from '../components/InfoCallout';
import {
  SubmitPsychiatristReviewMutation,
  SubmitPsychiatristReviewMutationVariables,
  SubmitPsychiatristReviewDocument,
} from '../documents/submitPsychiatristReview.generated';
import {
  SubmitInvoiceMutation,
  SubmitInvoiceMutationVariables,
  SubmitInvoiceDocument,
} from '../documents/submitInvoice.generated';

export enum SessionFeedbackTestID {
  Container = 'SessionFeedbackTestID.Container',
  SecondQuestionRadioGroup = 'SessionFeedbackTestID.SecondQuestionRadioGroup',
  ThirdQuestionRadioGroup = 'SessionFeedbackTestID.ThirdQuestionRadioGroup',
  BackButton = 'SessionFeedbackTestID.BackButton',
  SubmitButton = 'SessionFeedbackTestID.SubmitButton',
}

export const SessionFeedback: React.FC<InvoicingPageStepProps> = ({
  data,
  setData,
  prevStep,
  memberClient,
}) => {
  const styles = useStyles();

  const history = useHistory();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isModalShown, setIsModalShown] = useState<boolean>(false);

  const submit = useCallback(async () => {
    setIsSubmitting(true);

    if (data.visit) {
      await memberClient.mutate<SubmitInvoiceMutation, SubmitInvoiceMutationVariables>({
        mutation: SubmitInvoiceDocument,
        variables: {
          visitUuid: data.visit.uuid,
          state: data.visit.state,
          modalityType: data.visit.modalityType,
          cptCode: data.cptCode,
          primaryDiagnosisCode: data.diagnosisCodes[0],
          secondaryDiagnosisCodes: data.diagnosisCodes.slice(1),
        },
      });

      if (
        data.pammIsClinicallyIndicatedForTheClient !== null &&
        data.clientIsInterestedInPursuingPammServices !== null
      ) {
        await memberClient.mutate<
          SubmitPsychiatristReviewMutation,
          SubmitPsychiatristReviewMutationVariables
        >({
          mutation: SubmitPsychiatristReviewDocument,
          variables: {
            visitUuid: data.visit.uuid,
            pammIsClinicallyIndicatedForTheClient: data.pammIsClinicallyIndicatedForTheClient,
            clientIsInterestedInPursuingPammServices: data.clientIsInterestedInPursuingPammServices,
            clientNotInterestedInPammReason: data.clientNotInterestedInPammReason,
          },
        });
      }

      await memberClient.cache.reset();
      setIsModalShown(true);
    }

    setIsSubmitting(false);
  }, [data]);

  const canContinue = useMemo<boolean>(
    () =>
      data.clientIsInterestedInPursuingPammServices === false
        ? data.clientNotInterestedInPammReason !== undefined
        : true,
    [data],
  );

  return (
    <div data-testid={SessionFeedbackTestID.Container}>
      <div className={styles.header}>
        <HeadingText50>Add session feedback (optional)</HeadingText50>
        <BodyTextMedium>
          Feedback on your session is used to improve care delivery and is not used for performance
          evaluation. We keep this information confidential.
        </BodyTextMedium>
      </div>
      <InfoCallout data={data} />
      <RadioGroup
        testID={SessionFeedbackTestID.SecondQuestionRadioGroup}
        legend={
          <HeadingText20>
            The client is interested in pursuing or continuing psychiatry and medication management.
          </HeadingText20>
        }
        options={[
          { label: 'Yes', value: 'YES' },
          { label: 'No', value: 'NO' },
        ]}
        selected={
          data.clientIsInterestedInPursuingPammServices !== null
            ? data.clientIsInterestedInPursuingPammServices
              ? 'YES'
              : 'NO'
            : undefined
        }
        onChange={(value) =>
          setData((data) => ({
            ...data,
            clientIsInterestedInPursuingPammServices: value === 'YES',
            clientNotInterestedInPammReason: undefined,
          }))
        }
      />
      {data.clientIsInterestedInPursuingPammServices === false ? (
        <RadioGroup
          testID={SessionFeedbackTestID.ThirdQuestionRadioGroup}
          legend={
            <HeadingText20>
              If the client is not interested in psychiatry and medication management, why?
            </HeadingText20>
          }
          options={[
            {
              label:
                'The client prefers to pursue non-pharmacological treatment options at this time',
              value: 'PREFERS_NON_PHARMACOLOGICAL_TREATMENT',
            },
            {
              label:
                'The client prefers to pursue psychiatric care with another provider outside of Modern Health',
              value: 'PREFERS_OUTSIDE_PROVIERS',
            },
            {
              label: 'Reason not disclosed/explored',
              value: 'REASON_NOT_DISCLOSED_OR_EXPLORED',
            },
            {
              label: 'N/A',
              value: 'N_A',
            },
          ]}
          selected={data.clientNotInterestedInPammReason ?? undefined}
          onChange={(value) =>
            setData((data) => ({
              ...data,
              clientNotInterestedInPammReason: value,
            }))
          }
        />
      ) : null}

      <DeprecatedButtonGroup
        firstButtonText="Back"
        firstButtonTestId={SessionFeedbackTestID.BackButton}
        onFirstButtonClick={prevStep}
        secondButtonText="Submit"
        secondButtonTestId={SessionFeedbackTestID.SubmitButton}
        onSecondButtonClick={submit}
        secondButtonDisabled={!canContinue || isSubmitting}
        secondButtonLoading={isSubmitting}
      />

      {isModalShown ? (
        <Dialog open aria-labelledby="form-dialog-title" maxWidth="md">
          <DialogContent className={styles.cardBackground}>
            <HeadingText50>Your visit details have been submitted</HeadingText50>
            <BodyTextSmall>
              Don't forget to send a summary, resources and write a note!
            </BodyTextSmall>
            <DeprecatedButton size="medium" title="Go Back" onClick={history.goBack} />
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  );
};

const useStyles = makeStyles((theme) => ({
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    '& > *': {
      margin: 0,
    },
  },
  cardBackground: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    padding: 24,
    width: '573px',

    '& >*': {
      margin: 0,
    },
  },
}));
